import { BaseQueryOptions } from '@apollo/client'
import { graphql, ChildProps } from '@apollo/client/react/hoc'
import { get } from 'lodash'
import { ApplicationError } from '../../helpers/errors'
import { CFCentre, CollectionOfTags } from '../../../types/contentful'
import { WHATS_ON_FILTERS_QUERY } from './query'

export const query = WHATS_ON_FILTERS_QUERY

interface ControlsItem {
  backendTitle: string
  topicFiltersCollection: CollectionOfTags
  experienceFiltersCollection: CollectionOfTags
}

interface CentreResponse {
  linkedFrom: {
    whatsHappeningFiltersCollection: {
      items: ControlsItem[]
    }
  }
}

interface Response {
  data?: {
    error?: Error
    loading?: boolean
    cfCentre?: CentreResponse
  }
}

interface Variables {
  centreId: string
}

interface InputProps {
  centre: CFCentre
}

interface OutputProps {
  error?: Error
  loading?: boolean
  topicFilters?: string[]
  experienceFilters?: string[]
}

export const mapPropsToOptions = ({
  centre,
}: InputProps): BaseQueryOptions<Variables> => ({
  variables: {
    centreId: centre.sys.id,
  },
  errorPolicy: 'ignore',
  context: { version: 2 },
})

const propsFromCfCentre = (
  cfCentre: CentreResponse | undefined
): OutputProps => {
  const controls = get(cfCentre, [
    'linkedFrom',
    'whatsHappeningFiltersCollection',
    'items',
  ])
  if (!controls || controls.length === 0) {
    return { topicFilters: [], experienceFilters: [] }
  } else {
    const { topicFiltersCollection, experienceFiltersCollection } = controls[0]
    const getFilters = (coll: CollectionOfTags): string[] =>
      coll.items.map(tag => tag.title).filter(title => title)
    return {
      topicFilters: getFilters(topicFiltersCollection),
      experienceFilters: getFilters(experienceFiltersCollection),
    }
  }
}

export const mapDataToProps = (
  res?: Response
): ChildProps<OutputProps, Response, Variables> => {
  const data = res ? res.data : null
  if (!data) {
    return {
      error: new ApplicationError('No data'),
    }
  }
  const { error, loading, cfCentre } = data
  if (loading) {
    return { loading }
  } else if (error) {
    return { error: new ApplicationError(error) }
  }
  return propsFromCfCentre(cfCentre)
}

const withWhatsOnFilters = graphql<InputProps, Response, Variables>(
  WHATS_ON_FILTERS_QUERY,
  {
    options: mapPropsToOptions,
    props: mapDataToProps,
  }
)

export default withWhatsOnFilters
